import type * as z from "zod";
import type { createApiKeySchema } from "../../../../shared/mutators/createApiKey";
import { useQuery, useZero } from "@rocicorp/zero/react";
import { styled } from "goober";
import { nanoid } from "nanoid";
import { useEffect, useRef, useState } from "react";
import { generateApiKey, hashApiKey } from "../../../../shared/apiKeys";
import { sharedMutators } from "../../../../shared/mutators";
import { queries } from "../../../../shared/queries";
import { useHasAccess } from "../../../hooks/useHasAccess";
import { useResolveScopeName } from "../../../hooks/useResolveScopeName";
import { useTenantRoles } from "../../../hooks/useTenantRoles";
import { formatDateShort } from "../../../lib/formatTimestamp";
import { Box } from "../../Box";
import { Button } from "../../Button";
import { ConfirmModal } from "../../ConfirmModal";
import { Input } from "../../Input";
import { ListTable } from "../../ListTable";
import { ServiceAccountAvatar } from "../../ServiceAccountAvatar";
import { useMakeUrl, navigate, routes, useCurrentUrl } from "../../../lib/urls";
import { TabbedModal, type ModalTab } from "../../TabbedModal";
import { TenantRolesChecklist } from "../../TenantRolesChecklist";
import { Text } from "../../Text";
import {
  Card,
  FieldLabel,
  ListBox,
  NeutralIconBadge,
  SectionHeading,
} from "../shared";
import { DangerSection } from "../DangerSection";
import { collectEffectiveGrants } from "../lib/collectEffectiveGrants";
import { useOpenResource } from "../lib/useOpenResource";
import { ScopeAccessEditor } from "../ScopeAccessEditor";
import { inferScopeKind, isEditableScopeKind } from "../lib/inferScopeKind";
import { useScopeAccessCloseWarning } from "../../ScopeAccessCloseWarning";
import { PrincipalAccessTable } from "../PrincipalAccessTable";
import { CreateApiKeyModal } from "../ApiKeysSettings/CreateApiKeyModal";
import { AddFederationModal } from "./AddFederationModal";
import { GetTemporaryCredentialModal } from "./GetTemporaryCredentialModal";

const NameCell = styled("div")`
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
`;

const NameCellText = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

const SectionHeadingRow = styled("div")`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
`;

const SectionHeadingText = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

const TabPanel = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

type BufferedKey = Pick<
  z.infer<typeof createApiKeySchema>,
  "id" | "name" | "start" | "key"
>;
type BufferedFed = {
  id: string;
  providerId: string;
  providerName: string;
  subject: string;
  name: string;
};

type RenderKey = {
  id: string;
  name: string | null;
  start: string | null;
  createdAt: number | null;
  lastRequest: number | null;
  expiresAt: number | null;
};

type RenderFed = {
  id: string;
  name: string;
  providerName: string;
  subject: string;
  lastUsedAt: number | null;
};

type Tab = "settings" | "access";

const TABS = [
  { key: "settings", label: "Settings", icon: "settings-2" },
  { key: "access", label: "Access", icon: "lock" },
] as const;

export function ServiceAccountDetail({
  serviceAccountId,
  onClose,
}: {
  serviceAccountId: string | null;
  onClose: () => void;
}) {
  const makeUrl = useMakeUrl();
  const z = useZero();
  // In create mode (serviceAccountId === null) nothing is persisted until
  // "Create service account": the name, keys, and federations are buffered
  // locally and written in one go, since keys/federations reference a service
  // account id that doesn’t exist yet. Access is configured after creation.
  // `newId` is generated up front so the avatar color is stable.
  const isCreateMode = serviceAccountId === null;
  const [newId] = useState(() => nanoid(12));
  const [draft, setDraft] = useState<string | null>(null);
  const [bufferedKeys, setBufferedKeys] = useState<BufferedKey[]>([]);
  const [bufferedFeds, setBufferedFeds] = useState<BufferedFed[]>([]);

  const [accounts] = useQuery(queries.serviceAccounts());
  const account = serviceAccountId
    ? accounts.find((a) => a.id === serviceAccountId)
    : undefined;
  const canManage = useHasAccess(
    "service_account:manage",
    serviceAccountId ?? undefined
  );
  const canAssume = useHasAccess(
    "service_account:assume",
    serviceAccountId ?? undefined
  );
  const canAssignTenantRoles = useHasAccess("tenant:roles_manage");
  const canEdit = isCreateMode || canManage;
  const [grants, grantsResult] = useQuery(
    queries.roleGrantsByPrincipalServiceAccount({
      serviceAccountId: serviceAccountId ?? "",
    })
  );
  const [customRoles, rolesResult] = useQuery(queries.allRolesLite());
  const [apps] = useQuery(queries.connectedAppsLite());
  const { resolveScopeName } = useResolveScopeName();
  const { openResource } = useOpenResource();
  const [keys] = useQuery(
    queries.serviceAccountKeys({ serviceAccountId: serviceAccountId ?? "" })
  );

  const [showGenerateKey, setShowGenerateKey] = useState(false);
  const [showTempCredential, setShowTempCredential] = useState(false);
  const [showAddFederation, setShowAddFederation] = useState(false);
  const { tab } = useCurrentUrl(routes.modal.serviceAccount, {
    id: serviceAccountId ?? "",
  });
  const activeTab = (tab ?? "settings") as Tab;

  const nameRef = useRef<HTMLInputElement>(null);

  const [confirm, setConfirm] = useState<{
    title: string;
    description: string;
    confirmLabel: string;
    onConfirm: () => void;
  } | null>(null);

  // Autofocus the name when creating; the dialog grabs focus on open.
  useEffect(() => {
    if (!isCreateMode) return;
    const id = requestAnimationFrame(() => nameRef.current?.focus());
    return () => cancelAnimationFrame(id);
  }, [isCreateMode]);

  const customRoleById = new Map(customRoles.map((role) => [role.id, role]));
  const appsById = new Map(apps.map((a) => [a.id, a]));
  const accessGrants = collectEffectiveGrants(grants, new Map());
  const otherAccessGrants = accessGrants.filter(
    (g) => !isEditableScopeKind(inferScopeKind(g, customRoleById))
  );
  const { stagedGrantsRef, requestClose, closeWarningModal } =
    useScopeAccessCloseWarning({ singular: "resource", plural: "resources" });
  const guardClose = () => requestClose(onClose);

  const tenantRoleCommit = (
    builtinRoleIds: string[],
    customRoleIds: string[]
  ) => {
    if (!serviceAccountId) return;
    z.mutate(
      sharedMutators.setServiceAccountTenantRoles({
        serviceAccountId,
        builtinRoleIds,
        customRoleIds,
      })
    );
  };

  const {
    allTenantRoles,
    assignedBuiltinRoleIds,
    assignedCustomRoleIds,
    toggleTenantRole,
    loaded: tenantRolesReady,
  } = useTenantRoles({
    grants,
    onCommit: tenantRoleCommit,
    ready: grantsResult.type === "complete",
  });

  const addKey = async (name: string) => {
    const rawKey = generateApiKey();
    const hashedKey = await hashApiKey(rawKey);
    const start = rawKey.slice(0, 12);
    if (isCreateMode) {
      setBufferedKeys((prev) => [
        ...prev,
        { id: nanoid(), name, start, key: hashedKey },
      ]);
    } else if (account) {
      z.mutate(
        sharedMutators.createApiKey({
          id: nanoid(),
          name,
          start,
          key: hashedKey,
          spaceId: null,
          serviceAccountId: account.id,
        })
      );
    }
    return rawKey;
  };

  const removeKey = (id: string) => {
    if (isCreateMode)
      setBufferedKeys((prev) => prev.filter((k) => k.id !== id));
    else z.mutate(sharedMutators.deleteApiKey({ id }));
  };

  const removeFed = (id: string) => {
    if (isCreateMode)
      setBufferedFeds((prev) => prev.filter((f) => f.id !== id));
    else z.mutate(sharedMutators.deleteServiceAccountFederation({ id }));
  };

  const createServiceAccount = () => {
    const name = (draft ?? "").trim();
    if (!name) return;
    z.mutate(sharedMutators.createServiceAccount({ id: newId, name }));
    for (const k of bufferedKeys) {
      z.mutate(
        sharedMutators.createApiKey({
          id: k.id,
          name: k.name,
          start: k.start,
          key: k.key,
          spaceId: null,
          serviceAccountId: newId,
        })
      );
    }
    for (const f of bufferedFeds) {
      z.mutate(
        sharedMutators.createServiceAccountFederation({
          id: f.id,
          serviceAccountId: newId,
          providerId: f.providerId,
          name: f.name,
          subject: f.subject,
        })
      );
    }
    onClose();
  };

  const saveName = () => {
    if (!account || draft === null) return;
    const trimmed = draft.trim();
    if (trimmed && trimmed !== account.name) {
      z.mutate(
        sharedMutators.updateServiceAccount({ id: account.id, name: trimmed })
      );
    }
    setDraft(null);
  };

  if (!isCreateMode && !account) return null;

  const effectiveKeys: RenderKey[] = isCreateMode
    ? bufferedKeys.map((k) => ({
        id: k.id,
        name: k.name,
        start: k.start,
        createdAt: null,
        lastRequest: null,
        expiresAt: null,
      }))
    : keys.map((k) => ({
        id: k.id,
        name: k.name,
        start: k.start,
        createdAt: k.createdAt,
        lastRequest: k.lastRequest,
        expiresAt: k.expiresAt,
      }));

  const keyItems = effectiveKeys.map((k) => ({
    key: k.id,
    name: (
      <NameCell>
        <NeutralIconBadge iconName="key" />
        <NameCellText>
          <Text ellipsis variant="text-regular-md" color="var(--text-primary)">
            {k.name ?? "Service account key"}
          </Text>
          <Text ellipsis variant="text-small" color="var(--text-secondary)">
            {k.start ? `${k.start}…` : ""}
          </Text>
        </NameCellText>
      </NameCell>
    ),
    created: (
      <Text variant="text-small" color="var(--text-secondary)">
        {k.createdAt ? formatDateShort(k.createdAt) : "—"}
      </Text>
    ),
    lastUsed: (
      <Text variant="text-small" color="var(--text-secondary)">
        {k.lastRequest ? formatDateShort(k.lastRequest) : "never"}
      </Text>
    ),
    expires: (
      <Text variant="text-small" color="var(--text-secondary)">
        {k.expiresAt ? formatDateShort(k.expiresAt) : "never"}
      </Text>
    ),
    actions: (hasMouse: boolean) => (
      <div
        style={{ opacity: hasMouse ? 1 : 0 }}
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
      >
        <Button
          variant="secondary"
          icon="x"
          onClick={() =>
            isCreateMode
              ? removeKey(k.id)
              : setConfirm({
                  title: "Revoke API key",
                  description: `Any workload using “${k.name ?? "this key"}” will lose access immediately. This can’t be undone.`,
                  confirmLabel: "Revoke key",
                  onConfirm: () => removeKey(k.id),
                })
          }
        />
      </div>
    ),
  }));

  const effectiveFeds: RenderFed[] = isCreateMode
    ? bufferedFeds.map((f) => ({
        id: f.id,
        name: f.name,
        providerName: f.providerName,
        subject: f.subject,
        lastUsedAt: null,
      }))
    : (account?.federations ?? []).map((f) => ({
        id: f.id,
        name: f.name,
        providerName: f.provider?.name ?? "—",
        subject: f.subject,
        lastUsedAt: f.lastUsedAt,
      }));

  const federationItems = effectiveFeds.map((f) => ({
    key: f.id,
    name: (
      <NameCell>
        <NeutralIconBadge iconName="id-card-lanyard" />
        <Text ellipsis variant="text-regular-md" color="var(--text-primary)">
          {f.name}
        </Text>
      </NameCell>
    ),
    provider: (
      <Text ellipsis variant="text-small" color="var(--text-secondary)">
        {f.providerName}
      </Text>
    ),
    subject: (
      <Text ellipsis variant="text-small" color="var(--text-secondary)">
        {f.subject}
      </Text>
    ),
    lastUsed: (
      <Text variant="text-small" color="var(--text-secondary)">
        {f.lastUsedAt ? formatDateShort(f.lastUsedAt) : "never"}
      </Text>
    ),
    actions: (hasMouse: boolean) => (
      <div
        style={{ opacity: hasMouse ? 1 : 0 }}
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
      >
        <Button
          variant="secondary"
          icon="x"
          onClick={() =>
            isCreateMode
              ? removeFed(f.id)
              : setConfirm({
                  title: "Remove federation",
                  description: `Workloads federating via “${f.name}” will no longer be able to assume this service account.`,
                  confirmLabel: "Remove",
                  onConfirm: () => removeFed(f.id),
                })
          }
        />
      </div>
    ),
  }));

  const tabs: ModalTab<Tab>[] = isCreateMode
    ? [TABS[0]]
    : TABS.map((t) => ({
        ...t,
        to: makeUrl(routes.modal.serviceAccount, {
          id: serviceAccountId ?? "",
          tab: t.key,
        }),
      }));

  return (
    <>
      <TabbedModal
        icon={
          isCreateMode ? (
            <NeutralIconBadge iconName="bot" />
          ) : (
            <ServiceAccountAvatar seed={account?.id ?? newId} />
          )
        }
        title={
          isCreateMode ? "New service account" : (draft ?? account?.name ?? "")
        }
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={(next) => {
          if (serviceAccountId) {
            navigate(routes.modal.serviceAccount, {
              id: serviceAccountId,
              tab: next,
            });
          }
        }}
        onClose={guardClose}
        footerRight={
          isCreateMode ? (
            <div style={{ display: "flex", gap: 8 }}>
              <Button size="l" variant="subtle" onClick={onClose}>
                Cancel
              </Button>
              <Button
                size="l"
                onClick={createServiceAccount}
                disabled={!(draft ?? "").trim()}
              >
                Create service account
              </Button>
            </div>
          ) : (
            <Button size="l" onClick={guardClose}>
              Done
            </Button>
          )
        }
      >
        <TabPanel
          style={{ display: activeTab === "settings" ? "flex" : "none" }}
        >
          <Card>
            <FieldLabel>
              <Box horizontal={8} bottom={10}>
                <Text variant="text-small-md">Name</Text>
              </Box>
              <Input
                ref={nameRef}
                readOnly={account ? !canManage : false}
                placeholder="e.g. deploy-bot"
                value={draft ?? account?.name ?? ""}
                onChange={(e) => setDraft(e.target.value)}
                onBlur={saveName}
                onKeyDown={
                  isCreateMode
                    ? (e) => {
                        if (e.key === "Enter") createServiceAccount();
                      }
                    : undefined
                }
                style={{ width: "100%" }}
              />
            </FieldLabel>
          </Card>

          {!isCreateMode &&
            canAssignTenantRoles &&
            allTenantRoles.length > 0 && (
              <Card>
                <SectionHeading>
                  <Text variant="text-regular-md">Tenant roles</Text>
                  <Text variant="text-small" color="var(--text-secondary)">
                    Tenant-wide capabilities for this service account.
                  </Text>
                </SectionHeading>
                <TenantRolesChecklist
                  roles={allTenantRoles}
                  assignedBuiltinRoleIds={assignedBuiltinRoleIds}
                  assignedCustomRoleIds={assignedCustomRoleIds}
                  onToggle={toggleTenantRole}
                  disabled={!canManage || !tenantRolesReady}
                />
              </Card>
            )}

          <Card>
            <SectionHeading>
              <SectionHeadingRow>
                <SectionHeadingText>
                  <Text variant="text-regular-md">API keys</Text>
                  <Text variant="text-small" color="var(--text-secondary)">
                    Secret keys that let a script or service act as this service
                    account. Treat them like passwords.
                  </Text>
                </SectionHeadingText>
                {canEdit && (
                  <Button
                    size="l"
                    variant="secondary"
                    icon="plus"
                    onClick={() => setShowGenerateKey(true)}
                  />
                )}
              </SectionHeadingRow>
            </SectionHeading>
            {effectiveKeys.length > 0 && (
              <ListBox data-compact="true" style={{ flex: "none" }}>
                <ListTable
                  headers={{
                    name: "Name",
                    created: "Created",
                    lastUsed: "Last used",
                    expires: "Expires",
                    actions: "",
                  }}
                  columns={{
                    created: "content",
                    lastUsed: "content",
                    expires: "content",
                  }}
                  items={keyItems}
                />
              </ListBox>
            )}
          </Card>

          {!isCreateMode && canAssume && (
            <Card>
              <SectionHeading>
                <SectionHeadingRow>
                  <SectionHeadingText>
                    <Text variant="text-regular-md">Temporary credentials</Text>
                    <Text variant="text-small" color="var(--text-secondary)">
                      Get a short-lived key that acts as this service account.
                      It expires on its own — no need to revoke it.
                    </Text>
                  </SectionHeadingText>
                  <Button
                    size="l"
                    variant="secondary"
                    icon="key"
                    onClick={() => setShowTempCredential(true)}
                  />
                </SectionHeadingRow>
              </SectionHeading>
            </Card>
          )}

          <Card>
            <SectionHeading>
              <SectionHeadingRow>
                <SectionHeadingText>
                  <Text variant="text-regular-md">Federation</Text>
                  <Text variant="text-small" color="var(--text-secondary)">
                    Let a workload — like a GitHub Actions job — act as this
                    service account using a short-lived OIDC token, instead of a
                    stored key.
                  </Text>
                </SectionHeadingText>
                {canEdit && (
                  <Button
                    size="l"
                    variant="secondary"
                    icon="plus"
                    onClick={() => setShowAddFederation(true)}
                  />
                )}
              </SectionHeadingRow>
            </SectionHeading>
            {effectiveFeds.length > 0 && (
              <ListBox data-compact="true" style={{ flex: "none" }}>
                <ListTable
                  headers={{
                    name: "Name",
                    provider: "Provider",
                    subject: "Subject",
                    lastUsed: "Last used",
                    actions: "",
                  }}
                  columns={{
                    provider: "content",
                    subject: "flex",
                    lastUsed: "content",
                  }}
                  items={federationItems}
                />
              </ListBox>
            )}
          </Card>

          {!isCreateMode && canManage && account && (
            <DangerSection
              title="Delete service account"
              description="Deleting this service account immediately revokes its API keys, federations, and access. This can’t be undone."
              actionLabel="Delete service account"
              confirm={{
                confirmLabel: "Delete",
                description: `Deleting “${account.name}” immediately revokes its API keys, federations, and access. This can’t be undone.`,
                onConfirm: () => {
                  z.mutate(
                    sharedMutators.deleteServiceAccount({ id: account.id })
                  );
                  onClose();
                },
              }}
            />
          )}
        </TabPanel>

        {!isCreateMode && (
          <TabPanel
            style={{ display: activeTab === "access" ? "flex" : "none" }}
          >
            <Card>
              <SectionHeading>
                <Text variant="text-regular-md">Access</Text>
                <Text variant="text-small" color="var(--text-secondary)">
                  Spaces, connectors, and skills this service account can
                  access.
                </Text>
              </SectionHeading>
              <ScopeAccessEditor
                active={activeTab === "access"}
                grantsReady={
                  grantsResult.type === "complete" &&
                  rolesResult.type === "complete"
                }
                principal={{ serviceAccountId: serviceAccountId! }}
                directGrants={grants}
                customRoles={customRoles}
                resolveScopeName={resolveScopeName}
                canManage={canManage}
                stagedGrantsRef={stagedGrantsRef}
                onOpenResource={openResource}
                hideEmpty={otherAccessGrants.length > 0}
              />
              {otherAccessGrants.length > 0 && (
                <PrincipalAccessTable
                  grants={otherAccessGrants}
                  rolesMap={customRoleById}
                  resolveScopeName={resolveScopeName}
                  apps={appsById}
                  onOpenResource={openResource}
                />
              )}
            </Card>
          </TabPanel>
        )}
      </TabbedModal>
      {closeWarningModal}

      {showGenerateKey && (
        <CreateApiKeyModal
          alert
          showAccess={false}
          onClose={() => setShowGenerateKey(false)}
          onCreateKey={addKey}
        />
      )}
      {showTempCredential && account && (
        <GetTemporaryCredentialModal
          serviceAccountId={account.id}
          onClose={() => setShowTempCredential(false)}
        />
      )}
      {showAddFederation && (
        <AddFederationModal
          serviceAccountId={account?.id ?? ""}
          onClose={() => setShowAddFederation(false)}
          onAdd={
            isCreateMode
              ? (f) =>
                  setBufferedFeds((prev) => [...prev, { id: nanoid(12), ...f }])
              : undefined
          }
        />
      )}
      {confirm && (
        <ConfirmModal
          open
          onClose={() => setConfirm(null)}
          onConfirm={() => {
            confirm.onConfirm();
            setConfirm(null);
          }}
          title={confirm.title}
          description={confirm.description}
          confirmLabel={confirm.confirmLabel}
          destructive
        />
      )}
    </>
  );
}
